import { create } from 'zustand';
import { api, ApiError } from '../utils/api';
import { User } from '../types';
import { queryClient } from '../utils/queryClient';
import { browserSubscription } from '../utils/pushSupport';

// Сессия админа, отложенная на время входа под другим пользователем: без неё
// вернуться можно было только через выход и новый вход по коду.
const ADMIN_SESSION_KEY = 'adminSession';

interface AdminSession {
  token: string;
  name: string;
}

function loadAdminSession(): AdminSession | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(ADMIN_SESSION_KEY) || 'null');
    return parsed && typeof parsed.token === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

interface UserState {
  user: User | undefined;
  token: string | null;
  adminSession: AdminSession | null;
  setUser: (user: User | undefined) => void;
  setToken: (token: string | null) => void;
  /** Выход. `isSessionRejected` — сервер отверг токен (401): отписать и это устройство от пушей. */
  logout: (options?: { isSessionRejected?: boolean }) => void;
  checkSession: () => Promise<void>;
  impersonateUser: (user: User, token: string) => void;
  stopImpersonating: () => Promise<void>;
}

export const useUserStore = create<UserState>((set, get) => ({
  user: undefined,
  token: localStorage.getItem('token'),
  adminSession: loadAdminSession(),
  setUser: (user) => set({ user }),
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    set({ token });
  },
  logout: ({ isSessionRejected = false } = {}) => {
    localStorage.removeItem('token');
    localStorage.removeItem(ADMIN_SESSION_KEY);
    set({ user: undefined, token: null, adminSession: null });
    // Все компании на одном адресе: следующий вошедший (другой компании) не должен увидеть
    // ни строчки из кеша прошлой сессии.
    queryClient.clear();
    // Сессию отверг сервер (истекла, человека отключили или перенесли) — устройство больше не его:
    // иначе пуши прошлого человека приходили бы тому, кто войдёт следующим.
    if (isSessionRejected) {
      browserSubscription()
        .then((s) => s?.unsubscribe())
        .catch((e) => console.warn('[push] unsubscribe failed', e));
    }
  },
  checkSession: async () => {
    const token = get().token;
    if (!token) return;

    try {
      const user = await api('/users/me');
      set({ user });
    } catch (e) {
      // Logout only when the token is explicitly rejected (401). Network
      // errors and 5xx (typical during a backend redeploy) leave the token
      // intact so the next attempt can succeed once the backend comes back.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        // Истёк вход от имени (он живёт час) — назад в сессию админа, а не на страницу входа.
        if (get().adminSession) await get().stopImpersonating();
        else get().logout({ isSessionRejected: true });
      }
    }
  },
  impersonateUser: (user, token) => {
    // Вход под вторым пользователем подряд не должен затирать исходного админа.
    const current = get();
    const adminSession =
      current.adminSession ??
      (current.token ? { token: current.token, name: current.user?.name ?? '' } : null);
    if (adminSession) localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(adminSession));
    localStorage.setItem('token', token);
    set({ user, token, adminSession });
    // Кеш собран под своими правами — под чужими он не годится.
    queryClient.clear();
  },
  stopImpersonating: async () => {
    const saved = get().adminSession;
    if (!saved) return;
    localStorage.setItem('token', saved.token);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    set({ token: saved.token, adminSession: null });
    queryClient.clear();
    // Пользователя не обнуляем: иначе ProtectedRoute на миг увёл бы на /auth.
    await get().checkSession();
  },
}));
